import React, { useEffect } from "react"
import { Helmet } from "react-helmet"
import Navbar from "../../layout/Navbar"
import Footer from "../../layout/Footer"
import SEO, { getSEOData } from "../../components/SEO"
import { SITE_ORIGIN } from "../../utils/urls"

/**
 * /gst-invoice-for-cab-bookings/  -  a business-facing guide targeting the
 * "GST invoice for cab booking" / "corporate car rental GST invoice" cluster,
 * which the cab industry does not currently own (only tax publishers rank).
 * It funnels intent to the corporate cab service. Content is general GST
 * information with an explicit "not tax advice" disclaimer; no fabricated
 * numbers, rates stated as the standard CBIC positions with a verify-with-CA note.
 */

const CANONICAL_PATH = "/gst-invoice-for-cab-bookings/"

// Kept out of the index-cluttering "distance" style, this is one focused guide.
const FAQS = [
  {
    q: "What is the SAC code for cab and taxi services?",
    a: "Passenger transport services fall under SAC 9964. A GST-compliant cab invoice should carry this code so the expense is classified correctly in your books and in GSTR-2B.",
  },
  {
    q: "What GST rate applies to cab and taxi bookings?",
    a: "Passenger transport by a radio taxi or a rented motor vehicle is generally taxed at 5% GST where the operator does not claim input tax credit, or 12% where the operator does. Most cab operators bill at 5%. Rates are set by CBIC notifications and can change, so confirm the current rate on your invoice.",
  },
  {
    q: "When is GST on a cab booking payable under reverse charge (RCM)?",
    a: "Under the reverse-charge notifications for renting of motor vehicles designed to carry passengers (with fuel cost included), when the supplier is not a body corporate and does not charge 12%, and the recipient is a body corporate, the recipient pays the 5% GST directly to the government under RCM. If the supplier charges 12% under forward charge, RCM does not apply.",
  },
  {
    q: "Can a business claim input tax credit (ITC) on cab expenses?",
    a: "ITC on hiring or renting motor vehicles for transport of persons (seating up to 13, including the driver) is blocked under Section 17(5)(b) of the CGST Act, with exceptions, for example where you supply the same category of service onward, or where providing the transport to employees is obligatory under a law in force. Eligibility depends on your specific case, so confirm with your tax advisor.",
  },
  {
    q: "What must a GST-compliant cab invoice contain?",
    a: "As per Rule 46, it should show the operator's name, address and GSTIN, a unique invoice number and date, the customer's name, address and GSTIN (for B2B), the SAC code (9964), the description and taxable value, the GST rate and amount split into CGST and SGST or IGST, the place of supply, and a signature or digital signature.",
  },
  {
    q: "Does Pulpit provide GST invoices for corporate travel?",
    a: "Yes. Corporate bookings are billed with a proper GST invoice carrying our GSTIN, SAC 9964, the taxable value and the GST charged, plus monthly consolidated statements for easy reconciliation. Speak to our corporate desk to set up GST billing for your company.",
  },
]

const INVOICE_CHECKLIST = [
  "Operator's legal name, address and GSTIN",
  "Unique invoice number and date of issue",
  "Customer's name, address and GSTIN (for B2B claims)",
  "SAC code 9964 (passenger transport services)",
  "Description of the trip and the taxable value",
  "GST rate and amount, split as CGST + SGST or IGST",
  "Place of supply (state)",
  "Signature or valid digital signature of the supplier",
]

const GstInvoiceGuide = () => {
  const seo = getSEOData("gstInvoice")

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Corporate Cab Service", item: `${SITE_ORIGIN}/corporate-cab-services/` },
          { "@type": "ListItem", position: 3, name: "GST Invoice for Cab Bookings", item: `${SITE_ORIGIN}${CANONICAL_PATH}` },
        ],
      },
      {
        "@type": "Article",
        headline: "GST Invoice for Cab Bookings: A Guide for Businesses in India",
        description: seo.description,
        author: { "@type": "Organization", name: "Pulpit Cabs" },
        publisher: {
          "@type": "Organization",
          name: "Pulpit Cabs",
          logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/logo512.png` },
        },
        mainEntityOfPage: `${SITE_ORIGIN}${CANONICAL_PATH}`,
        inLanguage: "en-IN",
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  }

  return (
    <div className="bg-white text-[#171d1b] font-body">
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} canonical={CANONICAL_PATH} />
      <Helmet defer={false}>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-[#23aaa5] via-[#1f9c98] to-[#16807c] text-white">
        <svg aria-hidden="true" className="absolute inset-0 h-full w-full pointer-events-none" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 340" fill="none">
          <path d="M0 232 Q 300 132 600 208 T 1200 190 V340 H0 Z" fill="#ffffff" fillOpacity="0.09" />
          <path d="M0 270 Q 350 190 720 258 T 1200 250 V340 H0 Z" fill="#ffffff" fillOpacity="0.07" />
          <circle cx="1060" cy="72" r="140" fill="#ffffff" fillOpacity="0.05" />
        </svg>
        <div className="relative max-w-[820px] mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-14 md:pb-20">
          <span className="text-[11px] md:text-[12px] font-black uppercase tracking-[0.2em] text-white/85">For businesses</span>
          <h1 className="text-[30px] md:text-[46px] font-black tracking-tight leading-[1.06] mt-3 mb-4">
            GST Invoice for Cab Bookings <span className="text-[#ffd7cf]">in India</span>
          </h1>
          <p className="text-[15px] md:text-[18px] font-medium text-white/90 max-w-[640px]">
            What a GST-compliant cab invoice must contain, the rate that applies, when reverse charge kicks in, and when your business can claim input tax credit. A plain-English guide for finance and admin teams.
          </p>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Intro */}
        <p className="text-[16px] md:text-[17px] leading-relaxed text-[#3d4946] mb-6">
          Cab and taxi travel is a routine business expense, but it is only useful to your accounts if it comes with a correct GST invoice. A proper invoice lets you record the expense accurately, reconcile it in GSTR-2B, and claim input tax credit wherever your business is eligible. This guide covers what to look for, the rate that applies, and the reverse-charge and credit rules, all referenced to the standard CBIC positions.
        </p>

        {/* Section 1: invoice checklist */}
        <h2 className="text-[22px] md:text-[26px] font-black text-[#16211f] mt-10 mb-4">What a GST-compliant cab invoice must contain</h2>
        <div className="rounded-2xl border border-[#e6ecea] bg-[#f7fbfa] p-5 md:p-7">
          <ul className="space-y-3">
            {INVOICE_CHECKLIST.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] text-[#2b3835]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="3" className="mt-0.5 shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Section 2: rate */}
        <h2 className="text-[22px] md:text-[26px] font-black text-[#16211f] mt-10 mb-4">GST rate on cab &amp; taxi services</h2>
        <p className="text-[15px] md:text-[16px] leading-relaxed text-[#3d4946] mb-4">
          Passenger transport falls under SAC 9964. Two rate options exist for renting a motor vehicle or a radio/app taxi:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px] md:text-[15px] border-collapse">
            <thead>
              <tr className="bg-[#eef6f5] text-[#16211f]">
                <th className="text-left font-bold p-3 border border-[#e0ebe9]">Rate</th>
                <th className="text-left font-bold p-3 border border-[#e0ebe9]">Input tax credit to the operator</th>
                <th className="text-left font-bold p-3 border border-[#e0ebe9]">Typical use</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 border border-[#e0ebe9] font-bold">5%</td>
                <td className="p-3 border border-[#e0ebe9]">Not available to the operator</td>
                <td className="p-3 border border-[#e0ebe9]">Most radio / app cab bookings</td>
              </tr>
              <tr className="bg-[#fafcfc]">
                <td className="p-3 border border-[#e0ebe9] font-bold">12%</td>
                <td className="p-3 border border-[#e0ebe9]">Available to the operator</td>
                <td className="p-3 border border-[#e0ebe9]">Where the operator opts for forward charge with credit</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-[13.5px] text-[#6b7280] mt-3">Rates are set by CBIC notifications and can change. Confirm the current rate printed on your invoice.</p>

        {/* Section 3: RCM */}
        <h2 className="text-[22px] md:text-[26px] font-black text-[#16211f] mt-10 mb-4">Reverse charge (RCM) on renting motor vehicles</h2>
        <p className="text-[15px] md:text-[16px] leading-relaxed text-[#3d4946]">
          For renting of a motor vehicle designed to carry passengers, with the cost of fuel included, a specific reverse-charge rule applies. When the supplier is <span className="font-semibold">not a body corporate</span> and does not charge 12%, and the recipient <span className="font-semibold">is a body corporate</span>, the recipient pays the 5% GST directly to the government under RCM rather than to the supplier. If the supplier bills 12% under forward charge, RCM does not apply and the supplier collects the tax as usual. Knowing which applies matters for how your company records and pays the tax.
        </p>

        {/* Section 4: ITC */}
        <h2 className="text-[22px] md:text-[26px] font-black text-[#16211f] mt-10 mb-4">Can your business claim input tax credit?</h2>
        <p className="text-[15px] md:text-[16px] leading-relaxed text-[#3d4946]">
          This is where most companies over-claim. Under <span className="font-semibold">Section 17(5)(b)</span> of the CGST Act, input tax credit on hiring or renting motor vehicles for the transport of persons (seating capacity up to 13, including the driver) is <span className="font-semibold">blocked</span>, with limited exceptions:
        </p>
        <ul className="space-y-2.5 mt-4 mb-4">
          {[
            "Where you supply the same category of service onward (for example, you are yourself a cab or travel operator).",
            "Where the vehicle is used for further supply of such vehicles.",
            "Where providing the transport to employees is obligatory under a law in force at the time.",
          ].map((x) => (
            <li key={x} className="flex items-start gap-3 text-[15px] text-[#2b3835]">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#21A5A0] shrink-0" />
              <span>{x}</span>
            </li>
          ))}
        </ul>
        <p className="text-[15px] md:text-[16px] leading-relaxed text-[#3d4946]">
          Outside these cases, the GST on employee cab expenses is usually a cost, not a credit. Because eligibility turns on your exact facts, treat this as general guidance and confirm your position with your tax advisor.
        </p>

        {/* Section 5: Pulpit CTA */}
        <div className="rounded-[20px] bg-[#f0f6f5] border border-[#e0ebe9] p-6 md:p-8 mt-12">
          <h2 className="text-[20px] md:text-[24px] font-black text-[#16211f] mb-2">Get GST invoices for your corporate travel</h2>
          <p className="text-[15px] text-[#3d4946] mb-5">
            Pulpit bills every corporate booking with a proper GST invoice, our GSTIN, SAC 9964, the taxable value and the GST charged, plus monthly consolidated statements your finance team can reconcile in one place. Employee transport, airport transfers and monthly contracts across Ahmedabad, Gujarat and pan-India.
          </p>
          <a href="/corporate-cab-services/" className="inline-flex items-center gap-2 rounded-xl bg-[#21A5A0] text-white font-bold text-[15px] px-6 py-3.5 hover:bg-[#1D8D89] transition-colors no-underline">
            Explore corporate cab service
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </a>
        </div>

        {/* FAQ */}
        <h2 className="text-[22px] md:text-[26px] font-black text-[#16211f] mt-14 mb-5">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-[#e6ecea] bg-white p-5 open:bg-[#f7fbfa]">
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-bold text-[15px] md:text-[16px] text-[#16211f]">
                {f.q}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2.5" className="shrink-0 transition-transform group-open:rotate-180"><polyline points="6 9 12 15 18 9" /></svg>
              </summary>
              <p className="text-[14.5px] leading-relaxed text-[#3d4946] mt-3">{f.a}</p>
            </details>
          ))}
        </div>

        {/* Disclaimer */}
        <p className="text-[13px] text-[#6b7280] leading-relaxed mt-12 border-t border-[#eef2f1] pt-6">
          This guide is general information, not tax or legal advice. GST rates, notifications and credit rules change over time and depend on your specific facts. Verify the current position with your chartered accountant or the latest CBIC notifications before acting.
        </p>
      </main>

      <Footer />
    </div>
  )
}

export default GstInvoiceGuide
